import GroupsTree from 'components/CommonComponents/GroupsTree'
import { TagsList } from 'components/CommonComponents/TagsListModal'
import { UsersList } from 'components/CommonComponents/UsersListModal'
import { RootState } from 'constants/interfaces'
import strings from 'constants/strings'
import { useAudienceState } from 'hooks/useAudienceState'
import React, { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AudiencePublicationsModal from 'components/CommonComponents/AudiencePublicationModal'
import { WidgetWrapper } from 'components/TasksV2/UI/WidgetWrapper'
import { ErrorMessage } from 'components/ui/Input/ErrorMessage'
import { Box, Flex, Text } from '@chakra-ui/core'
import { getSplittedGroupNames } from 'components/NewsFeed/PostModal/utils'
import { Users } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import useLocaleText from 'components/useLocaleText'
import { isEmpty } from 'lodash'
import { StepType, useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { handleMuplipleIds } from 'components/CommonComponents/GroupsTree/utils'
import { clearError } from 'redux/actions/tasks_v2'
import LocaleText from 'components/LocaleText'

interface IProps {
  onClose: () => void
  isAudienceOptionsModalOpen: boolean
  setIsAudienceOptionsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

interface IAudience {
  groups: string[]
  users: string[]
  tags: string[]
}

export const AudienceModalOptions: React.FC<IProps> = ({
  onClose,
  isAudienceOptionsModalOpen,
  setIsAudienceOptionsModalOpen,
}) => {
  const { setValue, errors, watch, reset, triggerValidation } = useFormContext()
  const { formData } = useFormStepContext()
  const dispatch = useDispatch()
  const create_task_assignee_placeholder = useLocaleText('create_task_assignee_placeholder')

  const originalAudienceRef = useRef<IAudience | null>(null)

  const {
    groups: configGroups,
    retail_users: configRetailUsers,
    tags: configTags,
  } = useSelector((state: RootState) => state.config.config)

  const error = useSelector((state: RootState) => state.tasks_v2.error)

  const audience = watch('audience')

  const formDataAudience = formData[StepType.TASK_SETTINGS]?.audience

  const isFormDataAudienceEmpty =
    isEmpty(formDataAudience?.groups) && isEmpty(formDataAudience?.users) && isEmpty(formDataAudience?.tags)

  const getAudience = useMemo((): IAudience => {
    if (isFormDataAudienceEmpty) {
      return { groups: [], users: [], tags: [] }
    }
    return {
      groups: audience.groups,
      users: audience.users,
      tags: audience.tags,
    }
  }, [audience.groups, audience.tags, audience.users, isFormDataAudienceEmpty])

  const {
    selectedUsersUids,
    setSelectedUsersUids,
    selectedGroupsIds,
    setSelectedGroupsIds,
    selectedTagsIds,
    setSelectedTagsIds,
    groupsToPayload,
    setGroupsToPayload,
    namesForTooltip,
    filteredRerailUsersByTreeUsers,
    treeUsers,
    treeGroups,
    treeTags,
  } = useAudienceState({
    users: getAudience.users,
    groups: getAudience.groups,
    tagIds: getAudience.tags,
    selectedOptionType: 'TASK',
    isCalculateGroupsChildren: false,
  })

  const splittedGroupNames = useMemo(() => {
    return getSplittedGroupNames(namesForTooltip, 5)
  }, [namesForTooltip])

  const handleClose = async () => {
    const newAudience = {
      groups: Array.from(new Set(selectedGroupsIds)),
      users: selectedUsersUids,
      tags: selectedTagsIds,
    }

    setValue('audience', { ...newAudience })
    await triggerValidation('audience')
    onClose()
  }

  const handleBack = () => {
    if (originalAudienceRef.current) {
      setValue('audience', originalAudienceRef.current)
      setSelectedGroupsIds(originalAudienceRef.current.groups)
      setSelectedUsersUids(originalAudienceRef.current.users)
      setSelectedTagsIds(originalAudienceRef.current.tags)
      setGroupsToPayload(handleMuplipleIds(treeGroups, originalAudienceRef.current.groups))
      setTimeout(() => {
        triggerValidation('audience')
      }, 0)
    }
    setIsAudienceOptionsModalOpen(false)
  }

  const handleOpen = () => {
    originalAudienceRef.current = {
      groups: audience.groups || [],
      users: audience.users || [],
      tags: audience.tags || [],
    }

    setIsAudienceOptionsModalOpen(true)
    dispatch(clearError())
  }

  // Check for missing audience elements when there's a 403 error
  const missingAudienceElements = useMemo(() => {
    if (!error || error.status !== 403 || !selectedUsersUids || !selectedGroupsIds || !selectedTagsIds) {
      return null
    }

    const configUserIds = configRetailUsers?.map((user) => user.uid) || []
    const configGroupIds = configGroups ? Object.keys(configGroups) : []
    const configTagIds = configTags?.map((tag) => tag.sk) || []

    const missingUsers = selectedUsersUids.filter((uid) => !configUserIds.includes(uid))
    const missingGroups = selectedGroupsIds.filter((id) => !configGroupIds.includes(id))
    const missingTags = selectedTagsIds.filter((id) => !configTagIds.includes(id))

    // Get names for missing elements
    const missingUserNames = missingUsers.map((uid) => {
      const user = configRetailUsers?.find((u) => u.uid === uid)

      const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ')

      return fullName || uid
    })

    const missingGroupNames = missingGroups.map((id) => {
      const group = configGroups?.[id]

      return group?.name || id
    })

    const missingTagNames = missingTags.map((id) => {
      const tag = configTags?.find((t) => t.sk === id)

      return tag?.name || id
    })

    return {
      users: missingUserNames,
      groups: missingGroupNames,
      tags: missingTagNames,
      hasAny: missingUsers.length > 0 || missingGroups.length > 0 || missingTags.length > 0,
    }
  }, [error, selectedUsersUids, selectedGroupsIds, selectedTagsIds, configRetailUsers, configGroups, configTags])

  const renderViewOptionModal = (
    viewOption: number,
    setViewOption: React.Dispatch<React.SetStateAction<number>> | any
  ) => {
    switch (viewOption) {
      case 0:
        return (
          <GroupsTree
            isOpen={true}
            selectedGroupID={selectedGroupsIds}
            mode={strings.PARTIAL_MULTILPE_MODE}
            setViewOption={setViewOption}
            setSelectedGroupID={setSelectedGroupsIds}
            setGroupsToPayload={setGroupsToPayload}
            groupsToPayload={groupsToPayload}
            isAllowSubmitWhithoutGroups={true}
            treeGroups={treeGroups}
          />
        )
      case 1:
        return (
          <UsersList
            isOpen={true}
            onClose={onClose}
            setViewOption={setViewOption}
            selectedUsersUids={selectedUsersUids}
            setSelectedUsersUids={setSelectedUsersUids}
            treeUsers={filteredRerailUsersByTreeUsers}
          />
        )
      case 2:
        return (
          <TagsList
            isOpen={true}
            onClose={onClose}
            setViewOption={setViewOption}
            tags={treeTags}
            selectedTagsIds={selectedTagsIds}
            setSelectedTagsIds={setSelectedTagsIds}
          />
        )
      default:
        break
    }
  }

  useEffect(() => {
    if (isFormDataAudienceEmpty) {
      reset({
        audience: { groups: [], users: [], tags: [] },
      })
    }
    // eslint-disable-next-line
  }, [isFormDataAudienceEmpty])

  return (
    <>
      <Flex
        w="100%"
        alignItems="center"
        flexDir="row-reverse"
        style={{ columnGap: '10px' }}
        onClick={handleOpen}
        cursor="pointer"
      >
        <Flex alignItems="center" justifyContent="center" bg="#ababab" p="8px" borderRadius="full">
          <Users size={16} strokeWidth={3} color="white" />
        </Flex>
        <WidgetWrapper isError={!!errors.audience} p="10px" w="100%">
          <Text>{splittedGroupNames.length > 0 ? splittedGroupNames : create_task_assignee_placeholder}</Text>
        </WidgetWrapper>
      </Flex>

      {errors.audience && (
        <Box px="10px" pt="5px">
          <ErrorMessage errorMessage={errors.audience.message} />
        </Box>
      )}

      {missingAudienceElements?.hasAny && (
        <Box pt="10px">
          <Box border="1px solid #ff6b6b" borderRadius="8px" padding="12px" bg="#fff5f5">
            <Text fontSize="sm" fontWeight="600" color="#d63384" mb="8px">
              <LocaleText text="missing_elements" />
            </Text>

            {missingAudienceElements.users.length > 0 && (
              <Box mb="6px">
                <Text fontSize="xs" fontWeight="500" color="#d63384" mb="2px">
                  <LocaleText text="users" />
                </Text>
                <Text fontSize="xs" color="#666">
                  {missingAudienceElements.users.join(', ')}
                </Text>
              </Box>
            )}

            {missingAudienceElements.groups.length > 0 && (
              <Box mb="6px">
                <Text fontSize="xs" fontWeight="500" color="#d63384" mb="2px">
                  <LocaleText text="groups" />
                </Text>
                <Text fontSize="xs" color="#666">
                  {missingAudienceElements.groups.join(', ')}
                </Text>
              </Box>
            )}

            {missingAudienceElements.tags.length > 0 && (
              <Box mb="6px">
                <Text fontSize="xs" fontWeight="500" color="#d63384" mb="2px">
                  <LocaleText text="tags" />
                </Text>
                <Text fontSize="xs" color="#666">
                  {missingAudienceElements.tags.join(', ')}
                </Text>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {isAudienceOptionsModalOpen && (
        <AudiencePublicationsModal
          isOpen={isAudienceOptionsModalOpen}
          groups={treeGroups}
          users={treeUsers}
          onClose={handleClose}
          onBack={handleBack}
          setShowOptionsList={setIsAudienceOptionsModalOpen}
          renderViewOptionModal={renderViewOptionModal}
          selectedUsersUids={selectedUsersUids}
          selectedOptionType={'POLL'}
          selectedTagsIds={selectedTagsIds}
          groupsToPayload={selectedGroupsIds}
        />
      )}
    </>
  )
}
